/**
 * Notification Detection API
 * 
 * Aggregates all database health checks into a unified notification system:
 * - Uber income gaps (Migration 011)
 * - Accounting discrepancies (Migration 009 metrics)
 * - Data quality issues
 * - Missing required data
 */

import { supabase } from './supabase';
import {
  Notification,
  NotificationSummary,
  NotificationSeverity,
  NotificationCategory,
  UberGapNotification,
  AccountingIssueNotification,
  DataQualityNotification,
} from '../types/notifications';

// ============================================================
// UBER INCOME GAP NOTIFICATIONS
// ============================================================

export async function getUberGapNotifications(): Promise<UberGapNotification[]> {
  try {
    // TEMPORARILY DISABLED: Both the view 'uber_income_gaps_current' and RPC function 
    // 'detect_uber_income_gaps' have a schema error (using u.driver_name instead of u.name)
    // 
    // TODO: Fix the database function SQL to use correct column name:
    // Change: u.driver_name -> u.name in the detect_uber_income_gaps function
    // 
    // For now, return empty array to prevent console errors
    console.log('⚠️ Uber gap notifications temporarily disabled due to database schema error');
    return [];
    
    /* ORIGINAL CODE - RE-ENABLE AFTER DATABASE FIX:
    const { data, error } = await supabase.rpc('detect_uber_income_gaps', {
      p_start_date: null, // Last 90 days
      p_end_date: null
    });

    if (error) {
      console.error('Uber gap detection error:', error);
      return [];
    }
    if (!data || data.length === 0) return [];
    
    // Limit to 50 most important gaps
    const limitedData = data.slice(0, 50);

    // Convert gaps to notifications
    return limitedData.map((gap, index) => {
      const severityMap: Record<string, NotificationSeverity> = {
        'CRITICAL': 'critical',
        'HIGH': 'high',
        'MEDIUM': 'medium',
        'LOW': 'low',
      };

      const severity = severityMap[gap.severity] || 'medium';
      
      return {
        id: `uber_gap_${gap.driver_id}_${gap.missing_week_start}`,
        category: 'uber_gaps' as const,
        severity,
        title: `Missing Uber Data: ${gap.driver_name || 'Unknown Driver'}`,
        message: gap.recommended_action || `${gap.weeks_since_last_report} weeks without Uber income data`,
        timestamp: new Date().toISOString(),
        isRead: false,
        actionUrl: `/drivers/${gap.driver_id}`,
        actionLabel: 'View Driver',
        relatedEntityId: gap.driver_id,
        relatedEntityType: 'driver',
        count: gap.weeks_since_last_report,
        metadata: {
          driverId: gap.driver_id,
          driverName: gap.driver_name || 'Unknown',
          weeksWithoutData: gap.weeks_since_last_report,
          lastReportDate: gap.last_report_date,
          missingWeekStart: gap.missing_week_start,
          missingWeekEnd: gap.missing_week_end,
        },
      };
    });
    */
  } catch (error) {
    console.error('Error fetching Uber gap notifications:', error);
    return [];
  }
}

// ============================================================
// ACCOUNTING DISCREPANCY NOTIFICATIONS
// ============================================================

export async function getAccountingIssueNotifications(): Promise<AccountingIssueNotification[]> {
  try {
    // Query the materialized view from Migration 009
    // NOTE: View is called 'accounting_metrics', not 'driver_accounting_metrics_v2'
    const { data, error } = await supabase
      .from('accounting_metrics')
      .select('*')
      .order('period_year', { ascending: false })
      .order('period_month', { ascending: false })
      .limit(12); // Last 12 months

    if (error) {
      console.error('Accounting metrics query error:', error);
      return [];
    }
    if (!data || data.length === 0) return [];

    const notifications: AccountingIssueNotification[] = [];

    // Check for anomalies in the accounting metrics
    data.forEach((period) => {
      // High outstanding balance warning
      if (period.total_outstanding && period.total_outstanding > 5000) {
        notifications.push({
          id: `accounting_outstanding_${period.period_year}_${period.period_month}`,
          category: 'accounting_issues' as const,
          severity: period.total_outstanding > 10000 ? 'high' : 'medium',
          title: `High Outstanding Balance: ${period.period_year}-${period.period_month}`,
          message: `AED ${period.total_outstanding.toFixed(2)} in unpaid settlements`,
          timestamp: new Date().toISOString(),
          isRead: false,
          actionUrl: `/accounting`,
          actionLabel: 'View Accounting',
          relatedEntityId: `${period.period_year}-${period.period_month}`,
          relatedEntityType: 'period',
          metadata: {
            driverId: '', // Period-level, not driver-specific
            driverName: `${period.period_year}-${period.period_month}`,
            discrepancyType: 'balance',
            actualValue: period.total_outstanding,
          },
        });
      }

      // Disputed settlements warning
      if (period.disputed_settlements && period.disputed_settlements > 0) {
        notifications.push({
          id: `accounting_disputed_${period.period_year}_${period.period_month}`,
          category: 'accounting_issues' as const,
          severity: period.disputed_settlements > 5 ? 'high' : 'medium',
          title: `Disputed Settlements: ${period.period_year}-${period.period_month}`,
          message: `${period.disputed_settlements} settlements require review`,
          timestamp: new Date().toISOString(),
          isRead: false,
          actionUrl: `/accounting`,
          actionLabel: 'View Details',
          relatedEntityId: `${period.period_year}-${period.period_month}`,
          relatedEntityType: 'period',
          metadata: {
            driverId: '',
            driverName: `${period.period_year}-${period.period_month}`,
            discrepancyType: 'transaction',
          },
        });
      }
    });

    return notifications;
  } catch (error) {
    console.error('Error fetching accounting notifications:', error);
    return [];
  }
}

// ============================================================
// DATA QUALITY NOTIFICATIONS
// ============================================================

export async function getDataQualityNotifications(): Promise<DataQualityNotification[]> {
  try {
    const notifications: DataQualityNotification[] = [];

    // For now, return empty array - data quality checks can be added later
    // When needed, add custom SQL functions to detect:
    // - Orphaned vehicle assignments
    // - Duplicate transactions
    // - Invalid data references

    return notifications;
  } catch (error) {
    console.error('Error fetching data quality notifications:', error);
    return [];
  }
}

// ============================================================
// MISSING DATA NOTIFICATIONS
// ============================================================

export async function getMissingDataNotifications(): Promise<Notification[]> {
  try {
    const notifications: Notification[] = [];

    // For now, return empty array - can be enhanced later
    // Future enhancements could check for:
    // - Drivers with no recent Salik data
    // - Drivers with no recent Carem data
    // - Missing rent payments
    
    // Example implementation (commented out until needed):
    /*
    const today = new Date();
    const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const { data: activeDrivers } = await supabase
      .from('drivers')
      .select('id, full_name')
      .limit(100);

    if (activeDrivers) {
      for (const driver of activeDrivers) {
        const { data: salikRecords } = await supabase
          .from('salik_transactions')
          .select('id')
          .eq('driver_id', driver.id)
          .gte('transaction_date', lastWeek.toISOString().split('T')[0])
          .limit(1);

        if (!salikRecords || salikRecords.length === 0) {
          notifications.push({
            id: `missing_salik_${driver.id}`,
            category: 'missing_data' as const,
            severity: 'low',
            title: `No Salik Data: ${driver.full_name}`,
            message: 'No Salik transactions in the last 7 days',
            timestamp: new Date().toISOString(),
            isRead: false,
            actionUrl: `/drivers/${driver.id}`,
            actionLabel: 'View Driver',
            relatedEntityId: driver.id,
            relatedEntityType: 'driver',
            metadata: {
              dataType: 'salik',
              driverId: driver.id,
              periodStart: lastWeek.toISOString().split('T')[0],
              periodEnd: today.toISOString().split('T')[0],
            },
          });
        }
      }
    }
    */

    return notifications;
  } catch (error) {
    console.error('Error fetching missing data notifications:', error);
    return [];
  }
}

// ============================================================
// AGGREGATE ALL NOTIFICATIONS
// ============================================================

export async function getAllNotifications(): Promise<Notification[]> {
  try {
    const [
      uberGaps,
      accountingIssues,
      dataQuality,
      missingData,
    ] = await Promise.all([
      getUberGapNotifications(),
      getAccountingIssueNotifications(),
      getDataQualityNotifications(),
      getMissingDataNotifications(),
    ]);

    // Combine all notifications
    const allNotifications = [
      ...uberGaps,
      ...accountingIssues,
      ...dataQuality,
      ...missingData,
    ];

    // Sort by severity and timestamp
    const severityOrder: Record<NotificationSeverity, number> = {
      critical: 1,
      high: 2,
      medium: 3,
      low: 4,
      info: 5,
      success: 6,
    };

    return allNotifications.sort((a, b) => {
      const severityDiff = severityOrder[a.severity] - severityOrder[b.severity];
      if (severityDiff !== 0) return severityDiff;
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  } catch (error) {
    console.error('Error fetching all notifications:', error);
    return [];
  }
}

// ============================================================
// NOTIFICATION SUMMARY
// ============================================================

export async function getNotificationSummary(): Promise<NotificationSummary> {
  try {
    const notifications = await getAllNotifications();

    const summary: NotificationSummary = {
      total: notifications.length,
      unread: notifications.filter(n => !n.isRead).length,
      bySeverity: {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        info: 0,
        success: 0,
      },
      byCategory: {
        uber_gaps: 0,
        accounting_issues: 0,
        data_quality: 0,
        constraint_violations: 0,
        missing_data: 0,
        system: 0,
        payment_due: 0,
        maintenance_due: 0,
      },
    };

    notifications.forEach(notification => {
      summary.bySeverity[notification.severity]++;
      summary.byCategory[notification.category]++;
    });

    return summary;
  } catch (error) {
    console.error('Error getting notification summary:', error);
    return {
      total: 0,
      unread: 0,
      bySeverity: { critical: 0, high: 0, medium: 0, low: 0, info: 0, success: 0 },
      byCategory: {
        uber_gaps: 0,
        accounting_issues: 0,
        data_quality: 0,
        constraint_violations: 0,
        missing_data: 0,
        system: 0,
        payment_due: 0,
        maintenance_due: 0,
      },
    };
  }
}

// ============================================================
// CRITICAL NOTIFICATIONS ONLY
// ============================================================

export async function getCriticalNotifications(): Promise<Notification[]> {
  const allNotifications = await getAllNotifications();
  return allNotifications.filter(n => n.severity === 'critical' || n.severity === 'high');
}
